import React, {
  ComponentProps,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { View, TouchableOpacity, ViewProps } from "react-native";
import {
  BottomSheetBackdrop,
  BottomSheetModal,
  BottomSheetScrollView,
} from "@gorhom/bottom-sheet";
import type { BottomSheetBackdropProps } from "@gorhom/bottom-sheet";
import { X } from "lucide-react-native";
import { Text } from "../Text/Text";
import { twMerge } from "tailwind-merge";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";

type BottomSheetProps = Omit<
  ComponentProps<typeof BottomSheetModal>,
  "onChange" | "children" | "snapPoints" | "ref"
> & {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
  snapPoints?: (string | number)[];
  stickyFooter?: React.ReactNode;
};

const BottomSheetContext = createContext(
  {} as {
    open: boolean;
    onClose: () => void;
  },
);
const useBottomSheetContext = () => useContext(BottomSheetContext);

export function BottomSheet({
  open,
  onClose,
  children,
  snapPoints = ["50%", "90%"],
  stickyFooter,
  ...props
}: BottomSheetProps) {
  const bottomSheetRef = useRef<BottomSheetModal<never>>(null);
  const snapPointsMemo = useMemo(() => snapPoints, [snapPoints]);

  useEffect(() => {
    if (open) {
      // Use requestAnimationFrame to ensure smooth opening on iOS
      requestAnimationFrame(() => {
        bottomSheetRef.current?.present();
      });
    } else {
      bottomSheetRef.current?.close();
    }
  }, [open]);

  const renderBackdrop = (backdropProps: BottomSheetBackdropProps) => (
    <BottomSheetBackdrop
      {...backdropProps}
      opacity={0.5}
      pressBehavior="close"
      appearsOnIndex={0}
      disappearsOnIndex={-1}
    />
  );

  return (
    <BottomSheetContext.Provider value={{ open, onClose }}>
      <BottomSheetModal
        ref={bottomSheetRef}
        onChange={(index) => {
          if (index === -1) onClose();
        }}
        snapPoints={snapPointsMemo}
        backgroundStyle={{
          backgroundColor: "#0B1121",
          borderTopWidth: 1,
          borderTopColor: "rgba(255, 255, 255, 0.1)",
        }}
        backdropComponent={renderBackdrop}
        enablePanDownToClose
        handleIndicatorStyle={{ backgroundColor: "rgba(255, 255, 255, 0.3)" }}
        enableDismissOnClose
        {...props}
      >
        <View className="flex-1">
          <BottomSheetScrollView className="flex-1 p-4">
            {children}
          </BottomSheetScrollView>
          {stickyFooter}
        </View>
      </BottomSheetModal>
    </BottomSheetContext.Provider>
  );
}

type BottomSheetHeaderProps = ViewProps & {
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  disableClose?: boolean;
};

export function BottomSheetHeader({
  title,
  subtitle,
  className,
  disableClose = false,
  ...props
}: BottomSheetHeaderProps) {
  const { onClose } = useBottomSheetContext();
  const capturePress = usePressCapture("BottomSheetHeader");

  return (
    <View
      className={twMerge(
        "flex-row items-start justify-between border-b border-white/10 p-4 pt-0",
        className,
      )}
      {...props}
    >
      <View className="flex-1 gap-1">
        {typeof title === "string" ? (
          <Text variant="title" className="font-serif font-bold text-white">
            {title}
          </Text>
        ) : (
          title
        )}
        {typeof subtitle === "string" ? (
          <Text size="sm" className="text-slate-400">
            {subtitle}
          </Text>
        ) : (
          subtitle
        )}
      </View>
      {!disableClose && (
        <TouchableOpacity
          accessibilityRole="button"
          accessibilityLabel="Close"
          onPress={() => {
            // Icon-only affordance; its accessibility label is the copy.
            capturePress(pressLabel("Close"), "close_bottom_sheet");
            onClose();
          }}
          hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
          className="p-1"
        >
          <X size={16} color="#94a3b8" />
        </TouchableOpacity>
      )}
    </View>
  );
}

export function BottomSheetContent({ className, ...props }: ViewProps) {
  return <View className={twMerge("flex-1 gap-4 p-4", className)} {...props} />;
}

export function BottomSheetFooter({ className, ...props }: ViewProps) {
  return (
    <View
      className={twMerge(
        "flex-row gap-2 border-t border-white/10 p-4 [&>*]:flex-1",
        className,
      )}
      {...props}
    />
  );
}
